<template>
  <q-banner
    v-if="showMaintenance"
    class="bg-warning text-black">
    <div class="text-h5">
      Le serveur est actuellement en cours de maintenance!
      <br />
      Certaines fonctionnalités tel que l'envoi de photos peuvent être indisponibles jusqu'à
      {{ maintenanceEndTimeFormatted }}.
    </div>
  </q-banner>
</template>

<script lang="ts" setup>
import dayjs from 'dayjs'
import { QBanner } from 'quasar'
import { computed, onMounted, ref } from 'vue'

// Maintenance window configuration
const MAINTENANCE_HOUR = 5
const MAINTENANCE_START_MINUTE = 30
const MAINTENANCE_END_HOUR = 6
const MAINTENANCE_END_MINUTE = 0

// Maintenance banner logic
const now = ref(dayjs())
const showMaintenance = computed(() => {
  const currentHour = now.value.hour()
  const currentMinute = now.value.minute()

  // When maintenance starts and ends in the same hour
  if (MAINTENANCE_HOUR == MAINTENANCE_END_HOUR) {
    return currentHour === MAINTENANCE_HOUR && currentMinute >= MAINTENANCE_START_MINUTE && currentMinute < MAINTENANCE_END_MINUTE
  }

  // When maintenance spans different hours
  return (
    (currentHour === MAINTENANCE_HOUR && currentMinute >= MAINTENANCE_START_MINUTE) ||
    (currentHour === MAINTENANCE_END_HOUR && currentMinute < MAINTENANCE_END_MINUTE)
  )
})

// Format the end time for display
const maintenanceEndTimeFormatted = computed(() => {
  const hour = MAINTENANCE_END_HOUR.toString().padStart(2, '0')
  const minute = MAINTENANCE_END_MINUTE.toString().padStart(2, '0')
  return `${hour}:${minute}`
})

const scheduleNextCheck = () => {
  const current = dayjs()
  let nextCheck

  // Update current time first
  now.value = current

  // If we're in the maintenance window
  if (showMaintenance.value) {
    // Calculate time until maintenance ends
    nextCheck = current.hour(MAINTENANCE_END_HOUR).minute(MAINTENANCE_END_MINUTE).second(0)
  } else {
    // If we're before today's maintenance window
    if (current.hour() < MAINTENANCE_HOUR || (current.hour() === MAINTENANCE_HOUR && current.minute() < MAINTENANCE_START_MINUTE)) {
      nextCheck = current.hour(MAINTENANCE_HOUR).minute(MAINTENANCE_START_MINUTE).second(0)
    } else {
      // Schedule for next day
      nextCheck = current.add(1, 'day').hour(MAINTENANCE_HOUR).minute(MAINTENANCE_START_MINUTE).second(0)
    }
  }

  const msUntilNextCheck = nextCheck.diff(current)

  // Ensure we don't have negative intervals
  if (msUntilNextCheck > 0) {
    setTimeout(() => {
      scheduleNextCheck()
    }, msUntilNextCheck)
  } else {
    // If we somehow got a negative interval, check again in 1 second
    setTimeout(() => {
      scheduleNextCheck()
    }, 1000)
  }
}

onMounted(() => {
  // Do an immediate check and then start the schedule
  now.value = dayjs()
  scheduleNextCheck()
})
</script>
